import { useCallback, useContext } from 'react';
import type { ApplePayConfiguration } from '@adyen/adyen-web';
import { CheckoutContext } from '@/context/CheckoutContext';
import { buildApplePayTaxUpdate, getApplePayTotalLabel } from '@/utils/adyen';
import { shouldUseWalletDerivedZipTax } from '@/utils/tax';
import { logError } from '@/utils/logger';
import { ErrorLogs } from '@/constants/logs';
import { WalletZipTaxStatus } from '@/types/walletZipTax';
import { useWalletZipTaxCalculator } from './useWalletZipTaxCalculator';
import { resolveCheckoutGameName } from '@/utils/publisher';

type OnPaymentMethodSelected = NonNullable<ApplePayConfiguration['onPaymentMethodSelected']>;

/**
 * ACDEV-25067 (Adyen Apple Pay): Apple fires `onPaymentMethodSelected` with the
 * selected card's REDACTED `billingContact` (postal code, state, city, country)
 * before authorization — and per Apple's docs it also fires on sheet open for
 * the DEFAULT card — so we can recompute tax and patch the **open sheet**
 * total + line items via `resolve(update)`.
 *
 * Previously wired to `onShippingContactSelected`. Swapped to
 * `onPaymentMethodSelected` because:
 *   - the sheet no longer renders a "Shipping" section (our order is a digital
 *     good, so the section was UX noise);
 *   - every Wallet card has a billing address, so the pre-auth ZIP is more
 *     reliably present than the shipping-contact list (which can be empty or
 *     heavily redacted);
 *   - tax is a jurisdiction check against the card's billing address, so
 *     billing is the semantically correct signal.
 *
 * The handler MUST always `resolve` with a valid total, otherwise the sheet
 * hangs — so a missing/invalid ZIP or a failed tax lookup re-sends the current
 * total instead of bailing. The resolved ZIP is stashed in `capturedWalletTaxRef` for
 * the finalize step. We don't write to checkout context here: `useAdyenApplePay`
 * re-creates the component on `pricing` changes, which would unmount the live
 * sheet mid-flow.
 */
export const useAdyenApplePayBillingZipTax = () => {
  const { customer, pricing, featureFlags, sessionStartedWithoutValidTax, publisher, boot } =
    useContext(CheckoutContext);
  const { runWalletZipTax, capturedWalletTaxRef, applyWalletTaxToCheckout } = useWalletZipTaxCalculator();
  // Keep the in-sheet total row label consistent with the initial sheet: the
  // component is created with the company name as `totalPriceLabel`, and
  // every `resolve(update)` REPLACES the total row — resolving with the plain
  // 'Total' would visibly flip the label mid-sheet. Flag OFF (or no name)
  // falls back to 'Total', matching the component path.
  const totalLabel =
    getApplePayTotalLabel(
      resolveCheckoutGameName(boot?.gameName, publisher?.companyName || boot?.companyName),
      featureFlags
    ) ?? 'Total';
  // Mirror `useStripeWalletShippingZipTax` / `useGooglePayShippingZipTax`:
  // pair the callback's presence with the composite gate so a non-US/CA
  // mount OR an FF-OFF mount that also started with a valid tax
  // (ACDEV-27914) hands Adyen NO `onPaymentMethodSelected` at all — no
  // closure retained for an event the SDK will never fire
  // (`getApplePayComponentOptions` won't set `requiredBillingContactFields:
  // ['email', 'postalAddress']` for these sessions either).
  const shouldCollectZipForTax = shouldUseWalletDerivedZipTax(customer, featureFlags, sessionStartedWithoutValidTax);

  const onPaymentMethodSelected = useCallback<OnPaymentMethodSelected>(
    async (resolve, _reject, event) => {
      // Re-send the current total whenever we can't compute a new one so the
      // Apple Pay sheet never stalls on "loading".
      const currentUpdate = buildApplePayTaxUpdate(pricing.priceBreakdown, pricing, totalLabel);

      // Belt-and-suspenders outer try/catch: an unexpected throw from
      // `runWalletZipTax`, `buildApplePayTaxUpdate`, or `resolve()` itself
      // would otherwise leave the Apple Pay sheet spinning forever. Catch →
      // log via RUM → resolve with the current total. Guarded by
      // `hasResolved` so we never call `resolve()` twice (Adyen forbids it).
      let hasResolved = false;
      const safeResolve = (update: ReturnType<typeof buildApplePayTaxUpdate>) => {
        if (hasResolved) return;
        hasResolved = true;
        resolve(update);
      };

      const billingContact = event.paymentMethod?.billingContact;

      try {
        const outcome = await runWalletZipTax(billingContact?.postalCode, billingContact?.countryCode);

        // `runWalletZipTax` already logs `ERROR` outcomes to RUM via
        // `ErrorLogs.walletZipTaxError` — no double-logging here.

        if (outcome.status === WalletZipTaxStatus.SUCCESS) {
          safeResolve(buildApplePayTaxUpdate(outcome.result.priceBreakdown, pricing, totalLabel));
          return;
        }
        // `same_zip`, `unchanged`, `skipped`, `invalid`, `error`: total didn't
        // move (or we couldn't compute one), so resolve with the current update.
        safeResolve(currentUpdate);
      } catch (error) {
        logError(ErrorLogs.walletZipTaxHandlerError, error, {
          provider: 'adyen',
          wallet: 'apple_pay',
          walletCountry: billingContact?.countryCode?.toUpperCase() ?? '',
        });
        safeResolve(currentUpdate);
      }
    },
    [runWalletZipTax, pricing, totalLabel]
  );

  return {
    onPaymentMethodSelected: shouldCollectZipForTax ? onPaymentMethodSelected : undefined,
    capturedWalletTaxRef,
    applyWalletTaxToCheckout,
  };
};
